import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import SortableTable from '../SortableTable';
import { obfuscateUsername } from '../../pages/VisitsViewer';
import { SPECIAL_TYPE_LABELS } from '../../constants/visitsClassification';
import { universLabel } from '../../constants/visitsUnivers';

function VisitsTable({ visits, obfuscate, universSplit = false }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [filter, setFilter] = useState('');

  // Formatage de la durée (minutes → heures:minutes)
  const formatDuration = (minutes) => {
    if (!minutes) return '—';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
  };

  // Formatage de la date
  const formatDate = (date) => {
    if (!date) return '—';
    return format(date, 'dd/MM/yyyy HH:mm', { locale: fr });
  };

  // Filtrer les visites en fonction des filtres appliqués
  const filteredVisits = useMemo(() => {
    if (!filter && statusFilter === 'all') return visits;

    return visits.filter(visit => {
      // Filtre de recherche global
      const searchMatch = !filter ||
        visit.shopSign?.toLowerCase().includes(filter.toLowerCase()) ||
        visit.username?.toLowerCase().includes(filter.toLowerCase()) ||
        visit.univers?.toLowerCase().includes(filter.toLowerCase()) ||
        visit.interventionPoint?.toString().includes(filter);

      // Filtre par statut
      const statusMatch = statusFilter === 'all' ||
        (statusFilter === 'completed' && visit.date) ||
        (statusFilter === 'planned' && !visit.date && visit.plannedStart);

      return searchMatch && statusMatch;
    });
  }, [visits, filter, statusFilter]);

  // Définition des colonnes pour le SortableTable
  const columns = [
    {
      key: 'date',
      label: 'Date de visite',
      render: (visit) => formatDate(visit.date),
      // Fonction de tri personnalisée pour gérer les dates null
      sortFunction: (a, b, direction) => {
        const aDate = a.date;
        const bDate = b.date;

        if (!aDate && !bDate) return 0;
        if (!aDate) return direction === 'ascending' ? 1 : -1;
        if (!bDate) return direction === 'ascending' ? -1 : 1;

        return direction === 'ascending'
          ? aDate.getTime() - bDate.getTime()
          : bDate.getTime() - aDate.getTime();
      }
    },
    {
      key: 'plannedStart',
      label: 'Date planifiée',
      render: (visit) => {
        if (!visit.plannedStart) return '—';
        return format(visit.plannedStart, 'dd/MM/yyyy', { locale: fr });
      },
      // Fonction de tri personnalisée pour gérer les dates null
      sortFunction: (a, b, direction) => {
        const aDate = a.plannedStart;
        const bDate = b.plannedStart;

        if (!aDate && !bDate) return 0;
        if (!aDate) return direction === 'ascending' ? 1 : -1;
        if (!bDate) return direction === 'ascending' ? -1 : 1;

        return direction === 'ascending'
          ? aDate.getTime() - bDate.getTime()
          : bDate.getTime() - aDate.getTime();
      }
    },
    {
      key: 'shopSign',
      label: 'Enseigne',
      render: (visit) => visit.shopSign || '—'
    },
    // Only on a multi-univers project. There, a visit covering Blanc and Brun is two
    // rows on purpose — without this column they read as a duplicate bug.
    ...(universSplit ? [{
      key: 'univers',
      label: 'Univers',
      render: (visit) => universLabel(visit.univers),
    }] : []),
    {
      key: 'interventionPoint',
      label: 'Magasin',
      render: (visit) => (
        <span className="inline-flex items-center gap-1.5">
          {visit.interventionPoint || '—'}
          {visit.specialType && (
            <span
              className="px-1.5 inline-flex text-[10px] leading-4 font-semibold rounded-full bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200"
              title="Visite spéciale exclue des statistiques"
            >
              {SPECIAL_TYPE_LABELS[visit.specialType] || visit.specialType}
            </span>
          )}
        </span>
      )
    },
    {
      key: 'username',
      label: 'Effectif',
      render: (visit) => obfuscate ? obfuscateUsername(visit.username) : visit.username
    },
    {
      key: 'status',
      label: 'Statut',
      render: (visit) => (
        <span
          className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
          style={{
            backgroundColor: visit.statusColor + '20',
            color: visit.statusColor
          }}
        >
          {visit.statusLabel}
        </span>
      ),
      sortFunction: (a, b, direction) => {
        // Tri par statut
        const aValue = a.status || 0;
        const bValue = b.status || 0;

        return direction === 'ascending'
          ? aValue - bValue
          : bValue - aValue;
      }
    },
    {
      key: 'actualDurationMinutes',
      // The PHYSICAL visit's duration, repeated on each of its univers rows —
      // the one surface that shows it rather than the allocated share. Labelled
      // so a reader does not sum the column: two Blanc/Brun rows at 37 min are
      // one 37-minute visit, not 74.
      label: universSplit ? 'Durée (visite entière)' : 'Durée',
      render: (visit) => formatDuration(visit.actualDurationMinutes)
    }
  ];

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 items-center">
        <div className="w-full sm:w-1/3">
          <input
            type="text"
            placeholder="Rechercher..."
            className="w-full p-2 border rounded dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-1/3">
          <select
            className="w-full p-2 border rounded dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tous les statuts</option>
            <option value="completed">Visites effectuées</option>
            <option value="planned">Visites planifiées</option>
          </select>
        </div>

        <div className="w-full sm:w-1/3 text-right text-sm text-gray-600 dark:text-gray-400">
          Total: {filteredVisits.length} visite{filteredVisits.length !== 1 && 's'}
        </div>
      </div>

      {/* Tableau de visites utilisant SortableTable */}
      <SortableTable
        data={filteredVisits}
        columns={columns}
        emptyMessage="Aucune visite trouvée"
        countLabel={`Total: ${filteredVisits.length} visite${filteredVisits.length !== 1 ? 's' : ''}`}
        initialSortBy={{ key: 'date', direction: 'descending' }}
        exportable={true}
        exportFilename="visites_export.csv"
        disableFilters={true} // On utilise déjà nos propres filtres ci-dessus
      />
    </div>
  );
}

export default VisitsTable;